import { FactoryProvider, Injectable, InjectionToken, Injector } from "@angular/core";
import { ApiConfigurationService } from "../core/services/configuration.service";
import { BrowserAuthError, BrowserCacheLocation, InteractionRequiredAuthError, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication, RedirectRequest, SilentRequest } from "@azure/msal-browser";
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalRedirectComponent, MsalService } from "@azure/msal-angular";
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { firstValueFrom } from "rxjs";

export type AuthenticationSettings = {
  clientId: string;
  authority: string;
  authorityDomain: string;
}

export type ProtectedResource = {
  url: string;
  scopes: Array<string>;
}

var publicClientApplication: IPublicClientApplication;
var msalGuardConfiguration: MsalGuardConfiguration;
var msalInterceptorConfiguration: MsalInterceptorConfiguration;
var authenticationSettings: AuthenticationSettings;
var apiResource: ProtectedResource;

async function MSALInitialize(injector: Injector): Promise<void> {
  const apiConfigService = injector.get(ApiConfigurationService);
  await apiConfigService.ensureBootstrapLoaded();
  const bootstrap = apiConfigService.getBootstrapConfig();

  authenticationSettings = bootstrap.authenticationSettings;
  apiResource = bootstrap.apiResource;

  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(apiResource.url, apiResource.scopes);
  protectedResourceMap.set(bootstrap.globalCacheApiResource.url, bootstrap.globalCacheApiResource.scopes);

  publicClientApplication = new PublicClientApplication({
    auth: {
      clientId: authenticationSettings.clientId,
      authority: authenticationSettings.authority,
      knownAuthorities: [authenticationSettings.authorityDomain],
      redirectUri: `${window.location.origin}/`
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
      storeAuthStateInCookie: false
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Verbose,
        piiLoggingEnabled: false
      }
    }
  });

  msalGuardConfiguration = {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: apiResource.scopes
    },
    loginFailedRoute: '/'
  }

  msalInterceptorConfiguration = {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

var msalInstanceInitialized = false;

async function MsalEnsureInstanceInitialized(msalService: MsalService): Promise<void> {
  if (msalInstanceInitialized) {
    return;
  }

  await firstValueFrom(msalService.initialize());
  await msalService.instance.initialize();
  msalInstanceInitialized = true;
}

export async function MSALAcquireTokenSilent(msalService: MsalService, scopes: string[] = apiResource.scopes): Promise<string> {
  const request: SilentRequest = {
    scopes: scopes
  };

  const authenticationResult = await msalService.instance.acquireTokenSilent(request);
  return authenticationResult.accessToken;
}

export function MSALAcquireTokenRedirect(msalService: MsalService, scopes: string[] = apiResource.scopes) {
  const request: RedirectRequest = {
    scopes: scopes
  };

  msalService.acquireTokenRedirect(request);
}

export function MSALLoginRedirect(msalService: MsalService) {
  const request: RedirectRequest = {
    scopes: apiResource.scopes,
    state: JSON.stringify({ redirectUri: window.location.href })
  };

  msalService.loginRedirect(request);
}

export function MSALDoesAuthenticationFailureRequireRedirect(error: any): boolean {
  return error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError;
}

export async function MSALAuthenticate(injector: Injector, scopes: string[] = apiResource.scopes): Promise<string | undefined> {
  const msalService = injector.get(MsalService);
  await MsalEnsureInstanceInitialized(msalService);

  // Handle the redirect response if the user was redirected back to the application after authentication
  await msalService.instance.handleRedirectPromise();

  const accounts = msalService.instance.getAllAccounts();
  if (accounts.length > 0) {
    try {
      // Make sure the active account is set
      if (!msalService.instance.getActiveAccount()) {
        msalService.instance.setActiveAccount(accounts[0]);
      }

      return await MSALAcquireTokenSilent(msalService, scopes);
    }
    catch (error) {
      if (MSALDoesAuthenticationFailureRequireRedirect(error)) {
        MSALLoginRedirect(msalService);
      }
      else {
        console.error('Error acquiring token silently:', error);
      }
    }

  } else {
    MSALLoginRedirect(msalService);
  }

  return undefined;
}

function MSALInstanceFactory(): IPublicClientApplication {
  return publicClientApplication;
}

function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  return msalInterceptorConfiguration;
}

function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return msalGuardConfiguration;
}

export const AUTHENTICATION_SETTINGS = new InjectionToken('AuthenticationSettingsToken');
export const API_RESOURCE = new InjectionToken('ApiResourceToken');

export function authenticationSettingsFactory() {
  return authenticationSettings;
}

export function apiResourceFactory() {
  return apiResource;
}

const authenticationSettingsProvider: FactoryProvider = {
  provide: AUTHENTICATION_SETTINGS,
  useFactory: authenticationSettingsFactory
};

const apiResourceProvider: FactoryProvider = {
  provide: API_RESOURCE,
  useFactory: apiResourceFactory
};

export const AUTHENTICATION_SETTINGS_PROVIDERS = [
  authenticationSettingsProvider,
  apiResourceProvider
];

export const AUTHENTICATION_PROVIDERS = [
  MsalService,
  MsalBroadcastService,
  MsalRedirectComponent,
  {
    provide: MSAL_INSTANCE,
    useFactory: MSALInstanceFactory
  },
  {
    provide: MSAL_GUARD_CONFIG,
    useFactory: MSALGuardConfigFactory
  },
  {
    provide: MSAL_INTERCEPTOR_CONFIG,
    useFactory: MSALInterceptorConfigFactory
  },
  {
    provide: HTTP_INTERCEPTORS,
    useFactory: MsalInterceptor
  }
];

@Injectable({
  providedIn: 'root'
})
export class MsalLoaderService {
  constructor(private injector: Injector) { }

  async loadMsalModule(): Promise<string | undefined> {
    // Load the MsalModule manually to avoid circular dependencies
    const { MsalModule } = await import('@azure/msal-angular');

    await MSALInitialize(this.injector);

    const module = MsalModule.forRoot(MSALInstanceFactory(), MSALGuardConfigFactory(), MSALInterceptorConfigFactory());

    return await MSALAuthenticate(this.injector);
  }
}